import { axiosInstance } from '@/utils/libs/axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IUserSchema } from './user';
import { z } from 'zod';
import { ISelectOption } from '@/components/form/fields/SelectField';

type IRole = {
  id: string;
  role_name: string;
  type: string;
};

interface IUserItem {
  id: string;
  full_name: string;
}

type IContributorAndRole = {
  contributor: IUserSchema[];
  role: IRole[];
};

export type IAssignee = {
  assign: ISelectOption[];
};

export const projectAssignSchema = z.object({
  id: z.string().optional(),
  contributor_id:  z.array(z.string().uuid()),
  access: z.string(),
  project_id: z.string().optional(),
});

export type IProjectAssignSchema = z.infer<typeof projectAssignSchema>;

export const ProjectAssigneeService = {
  useGetContributorsAndRoles: (projectId: string) =>
    useQuery<IContributorAndRole>({
      queryKey: ['contributor', 'all', projectId],
      queryFn: async () => {
        const { data: userData } = await axiosInstance.get('/api/crud/user/all', {
          params: {
            filter: { project_id: projectId },
          },
        });
        const { data: roleData } = await axiosInstance.get('/api/crud/role/all');

        return { contributor: userData, role: roleData };
      },
    }),

  useGetOptions: (projectId?: string) =>
    useQuery<IAssignee>({
      queryKey: ['project-assignee-options'],
      queryFn: async () => {
        const response = await axiosInstance.get(
          '/api/crud/project-assignee/all',
          {
            params: {
              filter: { project_id: projectId },
            },
          },
        );

        const userOptions = response.data.all.filter((item: IUserItem) => item.full_name?.trim() && item.id).map((item: IUserItem) => ({
          value: item.id,
          label: item.full_name,
        }));

        return { assign: userOptions };
      },
    }),

  useGet: (id?: string) =>
    useQuery<IProjectAssignSchema>({
      queryKey: ['project-asginee', id],
      queryFn: async () => {
        if (!id) return {};

        const response = await axiosInstance.get(
          `/api/crud/project-assignee/${id}`,
        );

        return response.data?.data;
      },
    }),

  useCreate: () =>
    useMutation({
      mutationFn: async (data: IProjectAssignSchema) => {
        const response = await axiosInstance.post(
          `/api/crud/project-assignee`,
          data,
        );

        return response.data;
      },
    }),

  useDelete: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.delete(
          `/api/crud/project-assignee/${id}`,
        );

        return response.data;
      },
    }),

  useUpdate: () =>
    useMutation({
      mutationFn: async (data: IProjectAssignSchema) => {
        const response = await axiosInstance.post(
          `/api/crud/project-assignee/${data.id}`,
          data,
        );

        return response.data;
      },
    }),
};
